import { nanoid } from "nanoid";
import { getRecoil } from "recoil-nexus";
import Bugsnag from "@bugsnag/js";
import { consola } from "consola";

import firebase from "firebase/compat/app";
import "firebase/compat/analytics";
import "firebase/compat/storage";
import "firebase/compat/firestore";

import organizationIdState from "../atoms/organizationIdSelector";
import userDataState from "../atoms/userDataAtom";

async function setProtocolUpdatedOn({ organizationId, protocolId }) {
  try {
    const protocolDocRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocols")
      .doc(protocolId);
    consola.info("+++++ WRITE => ProtocolData: setProtocolUpdatedOn");
    await protocolDocRef.set(
      {
        updatedOn: new Date(),
      },
      { merge: true },
    );
  } catch (e) {
    Bugsnag.notify(e);
    throw e;
  }
}

async function setSplitDuplicateOffFlat({ organizationId, splitId }) {
  try {
    const splitDocRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocolSplits")
      .doc(splitId);
    consola.info("+++++ WRITE => ProtocolData: setSplitDuplicateOffFlat");
    await splitDocRef.set(
      {
        isDuplicate: false,
      },
      { merge: true },
    );
  } catch (e) {
    Bugsnag.notify(e);
    throw e;
  }
}

async function reorderExercises(protocolId, splitId) {
  const organizationId = getRecoil(organizationIdState);
  var batch = firebase.firestore().batch();

  const splitRef = firebase
    .firestore()
    .collection("organizations")
    .doc(organizationId)
    .collection("protocolExercises")
    .orderBy("index")
    .where("splitId", "==", splitId);
  consola.debug("++++++ READ -> ProtocolData: reorderExercises");
  const data = await splitRef.get();
  const docs = data.docs;

  let index = 0;
  docs.forEach((e) => {
    consola.info("+++++ WRITE => ProtocolData: reorderExercises");
    batch.set(
      e.ref,
      {
        index: index,
      },
      { merge: true },
    );
    index += 1;
  });
  consola.info("+++++ WRITE => ProtocolData: reorderExercises");
  await batch.commit();
  await setProtocolUpdatedOn({ organizationId, protocolId: protocolId });
}

async function setOrderOfExercises(protocolId, list) {
  const organizationId = getRecoil(organizationIdState);

  var batch = firebase.firestore().batch();

  let index = 0;

  list.forEach((e) => {
    const exerciseRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocolExercises")
      .doc(e.id);
    consola.info("+++++ WRITE => ProtocolData: setOrderOfExercises");
    batch.set(
      exerciseRef,
      {
        index: index,
      },
      { merge: true },
    );
    index += 1;
  });
  consola.info("+++++ WRITE => ProtocolData: setOrderOfExercises");
  await batch.commit();
  await setProtocolUpdatedOn({ organizationId, protocolId });
}

async function setOrderOfSplits(protocolId, list) {
  const organizationId = getRecoil(organizationIdState);

  var batch = firebase.firestore().batch();

  let index = 0;
  list.forEach((split) => {
    const splitRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocolSplits")
      .doc(split.id);
    consola.info("+++++ WRITE => ProtocolData: setOrderOfSplits");
    batch.set(
      splitRef,
      {
        index: index,
      },
      { merge: true },
    );
    index += 1;
  });
  consola.info("+++++ WRITE => ProtocolData setOrderOfSplits");
  await batch.commit();
  await setProtocolUpdatedOn({ organizationId, protocolId });
}

const ProtocolData = {
  getProtocol: async (protocolId, organizationId) => {
    consola.log(`data(): getProtocol`);

    // Prefer an explicit organizationId from the caller (e.g. client page route param).
    // Recoil can lag or disagree during navigation, which caused assigned-plan loads to
    // fail while active-plan loads succeeded (wrong org / missing doc).
    const orgId = organizationId || getRecoil(organizationIdState);

    if (!protocolId || !orgId) {
      consola.error(new Error("No protocol ID or organization ID"));
      return null;
    }
    const protocolRef = firebase
      .firestore()
      .collection("organizations")
      .doc(orgId)
      .collection("protocols")
      .doc(protocolId);

    consola.debug("++++++ READ -> ProtocolData: getProtocol");
    const protocol = await protocolRef.get();
    consola.debug("++++++ READ -> ProtocolData: getProtocol", protocol.data());
    if (protocol.exists) {
      return protocol;
    }
    return null;
  },
  getProtocols: async ({ filters = null, clientUID = null, includeBoth = false } = {}) => {
    consola.log(`data(): getProtocols`);
    const organizationId = getRecoil(organizationIdState);

    if (!organizationId) {
      consola.error(new Error("No organization ID"));
    }

    // If includeBoth is true, we need to query both templates and client-specific protocols
    if (includeBoth && clientUID) {
      const baseRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("protocols");

      let templatesRef = baseRef;
      let clientSpecificRef = baseRef;

      if (filters && filters.length > 0) {
        filters.forEach((filter) => {
          templatesRef = templatesRef.where(
            filter.field,
            filter.operation,
            filter.value,
          );
          clientSpecificRef = clientSpecificRef.where(
            filter.field,
            filter.operation,
            filter.value,
          );
        });
      }

      // Query templates (clientUID == null)
      templatesRef = templatesRef
        .where("clientUID", "==", null)
        .orderBy("name", "asc");

      // Query client-specific (clientUID == clientUID)
      clientSpecificRef = clientSpecificRef
        .where("clientUID", "==", clientUID)
        .orderBy("name", "asc");

      // Execute both queries in parallel
      const [templatesSnapshot, clientSpecificSnapshot] = await Promise.all([
        templatesRef.get(),
        clientSpecificRef.get(),
      ]);

      // Combine and deduplicate results
      const allDocs = [
        ...templatesSnapshot.docs,
        ...clientSpecificSnapshot.docs,
      ];

      // Remove duplicates by document ID
      const uniqueDocs = Array.from(
        new Map(allDocs.map((doc) => [doc.id, doc])).values(),
      );

      // Sort by name
      uniqueDocs.sort((a, b) => {
        const nameA = a.data().name || "";
        const nameB = b.data().name || "";
        return nameA.localeCompare(nameB);
      });

      consola.debug("++++++ READ -> ProtocolData: getProtocols (both)", uniqueDocs.length);
      return uniqueDocs;
    }

    // Original logic for single filter
    let protocolRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocols");

    if (filters && filters.length > 0) {
      filters.forEach((filter) => {
        protocolRef = protocolRef.where(
          filter.field,
          filter.operation,
          filter.value,
        );
      });
    }

    if (clientUID) {
      protocolRef = protocolRef.where("clientUID", "==", clientUID);
    } else {
      protocolRef = protocolRef.where("clientUID", "==", null);
    }

    protocolRef = protocolRef.orderBy("name", "asc");

    const reports = await protocolRef.get();
    const docs = reports.docs;

    consola.debug("++++++ READ -> ProtocolData: getProtcols", docs.length);

    return docs;
  },
  getProtocolsSubscription: async (uid, onChange) => {
    const organizationId = getRecoil(organizationIdState);
    const protocolRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocols")
      .orderBy("name", "asc");

    return protocolRef.onSnapshot((snapshot) => {
      consola.debug("++++++ SNAPSHOT -> ProtocolData: getProtocolsSubscription");
      if (onChange) {
        onChange(snapshot);
      }
    });
  },
  getProtocolSubscription: async ({
    protocolId,
    onChange,
    onSubscription,
    onError,
  }) => {
    consola.log(`data(): getProtocolSubscription`);
    const organizationId = getRecoil(organizationIdState);
    const protocolRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocols")
      .doc(protocolId);

    consola.debug("++++++ SNAPSHOT -> Protocol Data: getProtocolSubscription");
    const subscription = protocolRef.onSnapshot(
      (snapshot) => {
        if (onChange) {
          onChange(snapshot.data());
        }
      },
      (error) => {
        if (onError) {
          onError(error);
        }
      },
    );
    if (onSubscription) {
      onSubscription(subscription);
    }
  },
  getSplitsSubscriptionFlat: async (protocolId, onChange) => {
    consola.log(`data(): getSplitsSubscriptionFlat`);
    const organizationId = getRecoil(organizationIdState);
    const splitRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocolSplits")
      .orderBy("index")
      .where("protocolId", "==", protocolId);

    return splitRef.onSnapshot((snapshot) => {
      consola.info(
        "++++++ SNAPSHOT -> ProtocolData: getSplitsSubscriptionFlat",
      );
      if (onChange) {
        onChange(snapshot);
      }
    });
  },
  getProtocolSplits: async (protocolId) => {
    consola.log(`data(): getProtocolSplits`);
    const organizationId = getRecoil(organizationIdState);
    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocolSplits")
      .orderBy("index")
      .where("protocolId", "==", protocolId);

    consola.debug("++++++ READ -> ProtocolData: getProtocolSplits");
    return (await ref.get()).docs;
  },
  getSplitExercises: async (splitId) => {
    consola.log(`data(): getSplitExercises`);
    const organizationId = getRecoil(organizationIdState);
    const splitRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocolExercises")
      .orderBy("index")
      .where("splitId", "==", splitId);
    consola.debug("++++++ READ -> ProtocolData: getSplitExercises");
    return (await splitRef.get()).docs;
  },
  getSplitExerciseSubscriptionFlat: async (splitId, protocolId, onChange) => {
    const organizationId = getRecoil(organizationIdState);
    consola.log(
      `data(): getSplitExerciseSubscriptionFlat - splitId ${splitId}, organizationId ${organizationId}`,
    );
    const splitRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocolExercises")
      .orderBy("index")
      .where("protocolId", "==", protocolId)
      .where("splitId", "==", splitId);

    return splitRef.onSnapshot((snapshot) => {
      consola.info(
        "++++++ SNAPSHOT -> Protocol Data: getSplitExerciseSubscriptionFlat",
      );
      if (onChange) {
        onChange(snapshot);
      }
    });
  },
  addProtocol: async (values) => {
    const organizationId = getRecoil(organizationIdState);
    const protocolRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocols")
      .doc(values.id);

    const splitRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocolSplits")
      .where("protocolId", "==", values.id);

    consola.debug("++++++ READ -> ProtocolData: addProtocol");
    const data = await splitRef.get();
    const docs = data.docs;
    consola.info("+++++ WRITE => ProtocolData: addProtocol");
    await protocolRef.set(
      { splitCount: docs.length, ...values },
      { merge: true },
    );
    const newProtocol = await protocolRef.get();
    if (newProtocol.exists) {
      return newProtocol.data();
    }
    return null;
  },
  duplicateProtocol: async ({ protocolId, name = null, clientUID = null }) => {
    consola.log(`data(): ProtocolData - duplicateProtocol`);

    try {
      const userState = getRecoil(userDataState);

      if (!userState?.uid) {
        throw new Error("User not authenticated");
      }

      // Get Firebase auth token
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error("User not authenticated");
      }

      const token = await user.getIdToken();

      // Call the Cloud Function API - always use production functions
      const functionsUrl = "https://app.mighty45.com";

      const response = await fetch(
        `${functionsUrl}/api/v1/protocol/duplicate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            protocolId: protocolId,
            name: name,
            clientUID: clientUID,
          }),
        },
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || "Protocol duplication failed");
      }

      consola.info(
        "✅ Protocol duplicated successfully:",
        result.data.protocol.name,
      );

      // Return the protocol data in the expected format
      return {
        id: result.data.protocol.id,
        name: result.data.protocol.name,
        clientUID: result.data.protocol.clientUID,
        createdByUID: result.data.protocol.createdByUID,
        duplicatedFrom: result.data.protocol.duplicatedFrom,
        duplicatedOn: result.data.protocol.duplicatedOn,
        createdOn: result.data.protocol.createdOn,
        // Include stats for debugging/monitoring
        _stats: result.data.statistics,
      };
    } catch (error) {
      consola.error("❌ Protocol duplication failed:", error);
      Bugsnag.notify(error);
      throw error;
    }
  },
  duplicateSplit: async ({
    protocolId,
    split,
    count,
    showCopyAppend = true,
  }) => {
    consola.log(`data(): ProtocolData`);
    // REFACTOR: use batch() for the sets.
    const organizationId = getRecoil(organizationIdState);

    const newSplitId = nanoid();

    const result = await firebase.firestore().runTransaction(async () => {
      const currentSplitExercisesRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("protocolExercises")
        .where("splitId", "==", split.id);

      const exercisesDocs = await currentSplitExercisesRef.get();
      consola.debug("++++++ READ -> ProtocolData: suplicateSplit");

      const newSplitRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("protocolSplits")
        .doc(newSplitId);

      const newSplitData = {
        ...split,
        id: newSplitId,
        protocolId: protocolId,
        name: showCopyAppend ? `${split.name} (copy)` : `${split.name}`,
        isDuplicate: true,
        duplicatedOn: new Date(),
        index: count,
      };
      consola.info("+++++ WRITE => ProtocolData: duplicateSplit");
      await newSplitRef.set(newSplitData, { merge: true });

      // Add exercises to the new split.
      const promises = exercisesDocs.docs.map((exerciseDoc) => {
        const exerciseValue = exerciseDoc.data();
        exerciseValue.splitId = newSplitId;
        exerciseValue.protocolId = protocolId;
        exerciseValue.id = nanoid();

        if (exerciseValue.type === "super_set") {
          exerciseValue.exercises.forEach((e) => {
            e.splitId = newSplitId;
            e.protocolId = protocolId;
          });
        }

        const newExerciseRef = firebase
          .firestore()
          .collection("organizations")
          .doc(organizationId)
          .collection("protocolExercises")
          .doc(exerciseValue.id);

        return newExerciseRef.set(exerciseValue, { merge: true });
      });
      await Promise.all(promises);

      const protocolDocRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("protocols")
        .doc(protocolId);

      protocolDocRef.set(
        {
          splitCount: count + 1,
          updatedOn: new Date(),
        },
        { merge: true },
      );
    });

    await setProtocolUpdatedOn({ organizationId, protocolId: protocolId });

    return result;
  },
  addSplitFlat: async (values, isAdd = false) => {
    consola.log(`data(): ProtocolData`);
    const organizationId = getRecoil(organizationIdState);

    const batch = firebase.firestore().batch();

    const splitsRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocolSplits")
      .where("protocolId", "==", values.protocolId);

    consola.debug("++++++ READ -> ProtocolData: addSplitFlat");
    const data = await splitsRef.get();
    const docs = data.docs;

    const splitDocRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocolSplits")
      .doc(values.id);

    consola.info("+++++ WRITE => ProtocolData: addSplitFlat");
    batch.set(
      splitDocRef,
      {
        index: values.index ? values.index : docs.length,
        ...values,
        isDuplicate: false,
      },
      { merge: true },
    );

    if (isAdd) {
      // Update protocol split count
      const protocolDocRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("protocols")
        .doc(values.protocolId);

      batch.set(
        protocolDocRef,
        {
          splitCount: docs.length + 1,
          updatedOn: new Date(),
        },
        { merge: true },
      );
    }
    consola.info("+++++ WRITE => ProtocolData: addSplitFlat");
    await batch.commit();
    await setProtocolUpdatedOn({
      organizationId,
      protocolId: values.protocolId,
    });
  },
  addSplitExerciseFlat: async (values) => {
    // REFACTOR: Add batch and transaction code here.
    consola.log(`data(): ProtocolData`);
    const organizationId = getRecoil(organizationIdState);
    const exercisesCollectionRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocolExercises")
      .where("splitId", "==", values.splitId);

    consola.debug("++++++ READ -> ProtocolData: addSplitExerciseFlat");
    const data = await exercisesCollectionRef.get();
    const docs = data.docs;

    const exerciseDocRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocolExercises")
      .doc(values.id);
    consola.info("+++++ WRITE => ProtocolData: addSplitExerciseFlat");
    await exerciseDocRef.set(
      { index: values.index ? values.index : docs.length, ...values },
      { merge: true },
    );
    await setProtocolUpdatedOn({
      organizationId,
      protocolId: values.protocolId,
    });
    await setSplitDuplicateOffFlat({
      organizationId,
      protocolId: values.protocolId,
      splitId: values.splitId,
    });
  },
  deleteSplit: async (protocolId, splitId) => {
    consola.log(`data(): ProtocolData`);
    // REFACTOR: use batches.
    const organizationId = getRecoil(organizationIdState);
    const splitRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocolSplits")
      .doc(splitId);

    const splitExerciseRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocolExercises")
      .where("splitId", "==", splitId);

    // Delete the exercises
    // REFACTOR: use batches here.

    await firebase.firestore().runTransaction(async () => {
      consola.debug("++++++ READ -> ProtocolData: deleteSplit");
      const exercises = await splitExerciseRef.get();
      const promises = exercises.docs.map((e) => {
        return e.ref.delete();
      });
      await Promise.all(promises);

      // Delete the split.
      await splitRef.delete();

      // Re-order the splits.
      const splitsRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("protocolSplits")
        .orderBy("index", "asc")
        .where("protocolId", "==", protocolId);

      const protocolDocRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("protocols")
        .doc(protocolId);

      const splitsDocs = await splitsRef.get();

      // update split count in protocol.
      // REFACTOR: use batches here also.
      consola.info("+++++ WRITE => ProtocolData: deleteSplit");
      protocolDocRef.set(
        {
          splitCount: splitsDocs.docs.length,
          updatedOn: new Date(),
        },
        { merge: true },
      );

      let index = 0;
      splitsDocs.docs.forEach((doc) => {
        doc.ref.set(
          {
            index: index,
          },
          { merge: true },
        );
        index += 1;
      });
    });
  },

  deleteSplitExercise: async (values) => {
    console.log("ProtocolData.deleteSplitExercise called with:", values);
    const organizationId = getRecoil(organizationIdState);
    console.log("Organization ID:", organizationId);

    const exerciseDocRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocolExercises")
      .doc(values.id);

    console.log("Exercise document reference created:", exerciseDocRef.path);

    try {
      await firebase.firestore().runTransaction(async () => {
        console.log("Starting Firestore transaction...");
        await exerciseDocRef.delete();
        console.log("Exercise document deleted successfully");
        await reorderExercises(values.protocolId, values.splitId);
        console.log("Exercises reordered successfully");
        await setProtocolUpdatedOn({
          organizationId,
          protocolId: values.protocolId,
        });
        console.log("Protocol updated timestamp set successfully");
      });
      console.log("ProtocolData.deleteSplitExercise completed successfully");
    } catch (error) {
      console.error("ProtocolData.deleteSplitExercise failed:", error);
      throw error;
    }
  },
  deleteProtocol: async (protocolId, options = {}) => {
    consola.log(`data(): ProtocolData - deleteProtocol`);

    try {
      const userState = getRecoil(userDataState);

      if (!userState?.uid) {
        throw new Error("User not authenticated");
      }

      // Get Firebase auth token
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error("User not authenticated");
      }

      const token = await user.getIdToken();

      // Call the Cloud Function API - always use production functions
      const functionsUrl = "https://app.mighty45.com";

      const params = new URLSearchParams();
      if (options.archivedOnly) {
        params.set("archivedOnly", "true");
      }
      const qs = params.toString();
      const url = `${functionsUrl}/api/v1/protocol/${encodeURIComponent(protocolId)}${
        qs ? `?${qs}` : ""
      }`;

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        const err = new Error(result.error || "Protocol deletion failed");
        if (result.code) {
          err.code = result.code;
        }
        if (result.assignment) {
          err.assignment = result.assignment;
        }
        throw err;
      }

      consola.info(
        "✅ Protocol deleted successfully:",
        result.data.deletedProtocol.name,
      );

      // Return the deletion result in the expected format
      return {
        success: true,
        deletedProtocol: result.data.deletedProtocol,
        // Include stats for debugging/monitoring
        _stats: result.data.statistics,
      };
    } catch (error) {
      consola.error("❌ Protocol deletion failed:", error);
      Bugsnag.notify(error);
      throw error;
    }
  },
  setExerciseOrder: async (protocolId, list) => {
    await setOrderOfExercises(protocolId, list);
  },
  setSplitOrder: async (protocolId, list) => {
    await setOrderOfSplits(protocolId, list);
  },
};

export default ProtocolData;
